import api from './api';
import type { User } from '../types/user';
import type { Friendship } from '../types/friends';

class FriendsService {
  /**
   * Sends a friend request to a user by handle.
   */
  async sendFriendRequest(handle: string): Promise<Friendship> {
    try {
      const response = await api.post('/api/friends/request', { handle });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to send friend request');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Responds to a friend request.
   */
  async respondToFriendRequest(requesterId: string, accept: boolean): Promise<any> {
    try {
      const response = await api.post('/api/friends/respond', { requesterId, accept });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to respond to friend request');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Checks the friendship status between current user and another user.
   */
  async getFriendshipStatus(userId: string): Promise<{ status: 'FRIENDS' | 'PENDING_SENT' | 'PENDING_RECEIVED' | 'NONE' }> {
    try {
      const response = await api.get(`/api/friends/status/${userId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get friendship status');
    } catch (error: any) {
      // If error, assume no relationship
      console.warn('Error checking friendship status:', error);
      return { status: 'NONE' };
    }
  }

  /**
   * Gets a list of user's friends.
   */
  async getFriends(): Promise<User[]> {
    try {
      const response = await api.get('/api/friends');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get friends list');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Gets a list of pending friend requests.
   */
  async getPendingRequests(): Promise<Friendship[]> {
    try {
      const response = await api.get('/api/friends/pending');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get pending requests');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Gets a list of outgoing friend requests (sent by the current user).
   */
  async getOutgoingRequests(): Promise<Friendship[]> {
    try {
      const response = await api.get('/api/friends/outgoing');
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to get outgoing requests');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Cancels an outgoing friend request.
   */
  async cancelFriendRequest(addresseeId: string): Promise<any> {
    try {
      const response = await api.delete(`/api/friends/request/${addresseeId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to cancel friend request');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }

  /**
   * Removes a friend.
   */
  async removeFriend(friendId: string): Promise<any> {
    try {
      const response = await api.delete(`/api/friends/${friendId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || 'Failed to remove friend');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'An unexpected error occurred');
    }
  }
}

export default new FriendsService(); 